import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom, tap } from 'rxjs';
import { SystemFacade } from 'src/app/shared/system/system.facade';
import { environment } from 'src/environments/environment';
import { environment as envAlexania } from 'src/environments/environment.alexania';
import { environment as envManaus } from 'src/environments/environment.manaus';
import { environment as envETopocartNext } from 'src/environments/environment.next';
import { environment as envMesquita } from 'src/environments/environment.mesquita';
import { environment as envPortoVelho } from 'src/environments/environment.portovelho';
import { environment as envTopocartDev } from 'src/environments/environment.topocart.dev';
import { environment as envDesenvolvimento } from 'src/environments/environment.desenvolvimento';
import { AuthFacade } from '../../store/auth/auth.facade';
import {
  isRuralOviRoute,
  RURAL_OVI_PORTAL_KEY,
} from '../../types/projeto.types';

@Injectable()
export class AppInitService {
  private cityFromUrl: string | null = null;
  private readonly TOPC_DEV_CITY_KEY = 'topocartDevCity';

  constructor(
    private systemFacade: SystemFacade,
    private router: Router,
    private authFacade: AuthFacade
  ) {}

  async init() {
    if (
      isRuralOviRoute(window.location.pathname) &&
      !this.hasAuthenticatedUser()
    ) {
      return;
    }

    this.cityFromUrl = this.checkSubdomain();

    if (window.location.href.includes('/login')) {
      return;
    }

    try {
      this.systemFacade.getTenantConfig();
      const tconfig = await firstValueFrom(this.systemFacade.tenantConfig$);
      const publicTokens = await firstValueFrom(
        this.authFacade.publicTokens$.pipe(filter((p) => !!p))
      );

      const X_AUTH_TOKEN = publicTokens?.authToken;
      const X_TN_TOKEN = publicTokens?.tnToken;

      const tenantsConfigUrl = `${environment.apiUrl}tenants/config`;
      const res = await fetch(tenantsConfigUrl, {
        headers: {
          'X-AUTH-TOKEN': X_AUTH_TOKEN as string,
          'X-TN-TOKEN': X_TN_TOKEN as string,
        },
      });
      const data = await res.json();
      let config: any = null;
      try {
        if (data?.config && typeof data.config === 'string') {
          config = JSON.parse(data.config);
        } else {
          config = data?.config ?? null;
        }
      } catch (error) {
        console.error(
          '[AppInit] Erro ao fazer JSON.parse em data.config',
          data?.config,
          error
        );
        config = null;
      }
      const isProd = environment.production;

      const isETopocartDev = environment['isETopocartDev'];
      const isETopocartProd = environment['isETopocartProd'];
      const isNext = environment['isNext'];
      const isPortoVelho = environment['isPortoVelho'];
      const isAlexania = environment['isAlexania'];
      const isGoiania = environment['isGoiania'];
      const isManaus = environment['isManaus'];
      const isTopocartDev = environment['isTopocartDev'];
      const isDesenvolvimento = environment['isDesenvolvimento'];

      let cityName: string;
      const envMunicipio =
        typeof (environment as any).municipio === 'string'
          ? ((environment as any).municipio as string).trim()
          : '';

      if (envMunicipio) {
        cityName = envMunicipio;
      } else if (isTopocartDev && this.cityFromUrl) {
        cityName = this.cityFromUrl;
      } else if (!config?.cidade) {
        console.warn('[AppInit] Missing cidade in tenant config');
        return;
      } else {
        cityName = config?.cidade;
      }
      // environment.apiUrlPostgrest = `${environment.apiGatewayUrl}rest-db-${cityName}/`;

      // let apiUrlPostgrest=`https://${cityName}.${environment.postgRestUrl}`
      if (isDesenvolvimento) {
        this.setDesenvolvimentoEnv(cityName);
      } else if (environment['isTopocart']) {
        environment.apiUrlPostgrest = environment.apiUrlPostgrest.replace(
          '<placeholder>',
          cityName
        );
        environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
      } else {
        environment.apiUrlPostgrest = `https://${cityName}.${environment.postgRestUrl}`;
        environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
      }

      if (!isDesenvolvimento) {
        (
          environment as any
        ).apiUrlGenericPGTiles = `${environment.apiGatewayUrl}plataforma/tiles/`;
      }
      // environment.apiUrlPGTiles = `https://${cityName}.${environment.pgtilesUrl}`;

      // Porto Velho
      if (isPortoVelho) {
        this.setPortoVelhoEnv(cityName, isPortoVelho);
      }

      // Alexania
      if (isAlexania) {
        this.setAlexaniaEnv(cityName, isAlexania);
      }
      // Goiania
      if (isGoiania) {
        this.setAlexaniaEnv(cityName, isGoiania);
      }

      // Manaus
      if (isManaus) {
        this.setManausEnv(cityName, isManaus);
      }

      // ETOPOCART PROD
      if (isETopocartProd) {
        this.setMesquitaEnv(cityName, isETopocartProd);
      }

      // ETOPOCART DEV
      if (isNext) {
        this.setETopocartNextEnv(cityName, isNext);
      }

      // TOPOCART DEV
      if (isTopocartDev && !isDesenvolvimento) {
        this.setTopocartDevEnv(cityName, isTopocartDev);
      }
    } catch (error) {
      console.error('[AppInit] init error', error);
    }
  }

  checkSubdomain(): string | null {
    const isTopocartDev = environment['isTopocartDev'];
    let city: string | null = null;

    if (environment.production) {
      let site = window.location.href;
      if (site.includes('www.')) {
        site = site.replace('www.', '');
      }

      const splitSymbol = site.includes('-portal') ? '-' : '.';

      const subdomain = site.split(splitSymbol)[0];
      city = subdomain.split('//').pop() as string;

      if (city === 'portalgeo') {
        city = 'brusque';
      }

      // this.router.events
      // .pipe(
      //   filter((event) => event instanceof NavigationEnd),
      //   tap({
      //     next: ({ url }: NavigationEnd) => {
      //       if (url.includes('manutencao')) {
      //         return;
      //       }

      //       if (city === 'brusque' && url.includes('icad')) {
      //         this.router.navigate(['manutencao']);
      //       }
      //     },
      //   })
      // )
      // .subscribe();
    } else {
      if (isTopocartDev) {
        const pathname = window.location.pathname || '';
        const urlMatch = pathname.match(/^\/([^/]+)\/(icad|ovi|ovi-rural)(\/|$)/);
        if (urlMatch) {
          city = urlMatch[1];
          sessionStorage.setItem(this.TOPC_DEV_CITY_KEY, city);
        } else {
          const pathSegments = pathname
            .split('/')
            .filter((segment) => segment.length > 0);
          const firstSeg = pathSegments[0];

          const reserved = new Set([
            'icad',
            'ovi',
            RURAL_OVI_PORTAL_KEY,
            'auth',
            'select-mode',
            'manutencao',
            'simples',
            'not-found',
          ]);

          if (firstSeg && !reserved.has(firstSeg)) {
            city = firstSeg;
            sessionStorage.setItem(this.TOPC_DEV_CITY_KEY, city);
          } else {
            const storedCity = sessionStorage.getItem(this.TOPC_DEV_CITY_KEY);
            if (
              storedCity &&
              (firstSeg === 'icad' ||
                firstSeg === 'ovi' ||
                firstSeg === RURAL_OVI_PORTAL_KEY)
            ) {
              const full = `/${storedCity}${window.location.pathname}${window.location.search}${window.location.hash}`;
              if (!window.location.pathname.startsWith(`/${storedCity}/`)) {
                this.router.navigateByUrl(full);
              }
              city = storedCity;
            }
          }
        }
      } else {
        let cityName: string;
        // cityName = 'ouv_test';
        // cityName = 'new_test';
        // cityName = 'portovelho';
        // cityName = 'manaus';
        // cityName = 'rincao';
        // cityName = 'parauapebas';
        // cityName = 'ibiruba';
        // cityName = 'riobranco';
        // cityName = 'santacruzdosul';
        // cityName = 'imbituba';
        // cityName = 'vitoriadaconquista';
        // cityName = 'ararangua';
        // cityName = 'chopinzinho';
        // cityName = 'criciuma';
        // cityName = 'balneariocamboriu';
        // cityName = 'aparecidadegoiania';
        // cityName = 'lauromull/er';
        // cityName = 'limoeiro';
        // cityName = 'caxiasdosul';
        // cityName = 'teresina';
        cityName = 'brusque';
        // cityName = 'ribeiraodasneves';
        // cityName = 'portalgeo';
        // cityName = 'urussanga';
        // cityName = 'horizonte';
        // cityName = 'bnau';
        // cityName = 'imbituba';
        // cityName = 'alexania';
        // cityName = 'maracanau';
        // cityName = 'goiania'
        // cityName = 'lagarto'

        let site = `https://www.${cityName}-portal.geo360.com.br`;

        // let site = `https://www.${cityName}.brusque.sc.gov.br`;

        if (site.includes('www.')) {
          site = site.replace('www.', '');
        }
        const subdomain = site.split('-')[0];
        city = subdomain.split('//').pop() as string;
      }

      if (city === 'portalgeo') {
        city = 'brusque';
      }

      // this.router.events
      // .pipe(
      //   filter((event) => event instanceof NavigationEnd),
      //   tap({
      //     next: ({ url }: NavigationEnd) => {
      //       if (url.includes('manutencao')) {
      //         return;
      //       }

      //       if (city === 'brusque' && url.includes('icad')) {
      //         this.router.navigate(['manutencao']);
      //       }
      //     },
      //   })
      // )
      // .subscribe();
    }
    if (!city) {
      this.router.navigate(['not-found', '']);
      return null;
    }

    const previousCity = localStorage.getItem('currentCity');
    if (previousCity && previousCity !== city) {
      localStorage.clear();
      sessionStorage.clear();
    }
    localStorage.setItem('currentCity', city);

    this.authFacade.getPublicTokens(city);
    return city;
  }

  private hasAuthenticatedUser(): boolean {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '');
      return (
        !!currentUser &&
        typeof currentUser.authToken === 'string' &&
        typeof currentUser.tnToken === 'string'
      );
    } catch {
      return false;
    }
  }

  setETopocartNextEnv(cityName: string, isETopocartNext: boolean) {
    const cityPostgREST = envETopocartNext.apiUrlPostgrest.replace(
      '<placeholder>',
      cityName
    );

    environment.apiUrlPostgrest = isETopocartNext
      ? cityPostgREST
      : environment.apiUrlPostgrest;

    environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
  }

  setMesquitaEnv(cityName: string, isETopocartProd: boolean) {
    const cityPostgREST = envMesquita.apiUrlPostgrest.replace(
      '<placeholder>',
      cityName
    );

    environment.apiUrlPostgrest = isETopocartProd
      ? cityPostgREST
      : environment.apiUrlPostgrest;

    environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
  }

  setPortoVelhoEnv(cityName: string, isPortoVelho: boolean) {
    const cityPostgREST = envPortoVelho.apiUrlPostgrest.replace(
      '<placeholder>',
      cityName
    );

    environment.apiUrlPostgrest = isPortoVelho
      ? cityPostgREST
      : environment.apiUrlPostgrest;

    environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
  }

  setAlexaniaEnv(cityName: string, isAlexania: boolean) {
    const cityPostgREST = envAlexania.apiUrlPostgrest.replace(
      '<placeholder>',
      cityName
    );

    environment.apiUrlPostgrest = isAlexania
      ? cityPostgREST
      : environment.apiUrlPostgrest;

    environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
  }

  setManausEnv(cityName: string, isManaus: boolean) {
    const cityPostgREST = envManaus.apiUrlPostgrest.replace(
      '<placeholder>',
      cityName
    );

    environment.apiUrlPostgrest = isManaus
      ? cityPostgREST
      : environment.apiUrlPostgrest;

    environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
  }

  setDesenvolvimentoEnv(cityName: string) {
    const development = envDesenvolvimento as any;
    const replaceCity = (url: string) => url.replace('<placeholder>', cityName);

    environment.apiUrlPostgrest = replaceCity(development.apiUrlPostgrest);
    environment.apiUrlPGTiles = replaceCity(development.apiUrlPGTiles);
    environment.apiUrlGenericPostgrest = development.apiUrlGenericPostgrest;
    (environment as any).apiUrlGenericPGTiles =
      development.apiUrlGenericPGTiles;
    environment.postgRestUrl = replaceCity(development.postgRestUrl);
    environment.pgtilesUrl = replaceCity(development.pgtilesUrl);
    environment.apiGatewayUrl = development.apiGatewayUrl;
    (environment as any).apiGatewayRestPrefix =
      development.apiGatewayRestPrefix;
  }

  setTopocartDevEnv(cityName: string, isTopocartDev: boolean) {
    const cityPostgREST = envTopocartDev.apiUrlPostgrest.replace(
      '<placeholder>',
      cityName
    );

    environment.apiUrlPostgrest = isTopocartDev
      ? cityPostgREST
      : environment.apiUrlPostgrest;

    environment.apiUrlPGTiles = `${environment.apiGatewayUrl}tiles/`;
  }
}
